<template>
  <transition v-if="product" name="fade">
    <div
      v-if="!isCtaVisible"
      class="sticky bottom-0 z-1 w-full bg-white shadow-top "
      :style="`--un-bg-opacity: ${$viewport.lg ? '90%' : '100%'}; top: ${stickyHeaderTop}`"
    >
      <base-sticky name="stickyProductCta">
        <div
          ref="stickyContainerRef"
          class="flex items-center gap-4 p-4 lg:px-6 lg:py-2 "
          :class="{ wrap: showStickyTitle }"
          style="min-height: 4rem"
        >
          <base-picture
            v-if="galleryItems[0].src"
            :alt="galleryItems[0].alt"
            class="h-16 w-a shrink-0 <lg:hidden"
            :height="stickyHeader?.thumbnail.height"
            :src="galleryItems[0].src"
            :width="stickyHeader?.thumbnail.width"
          />
          <template v-if="showStickyTitle">
            <p class="subtitle-3 lg:grow lg:subtitle-4 " data-test-id="product-name">
              {{ product.name }}
            </p>
          </template>
          <div class="<lg:w-full">
            <template v-if="isComingSoon || isOutOfStock">
              <product-notify-me v-if="isNotifyMe" style="min-width: 12.5rem" />
              <vf-button
                v-else
                class="w-full"
                disabled
                :loading="pending"
                style="min-width: 12.5rem"
              >
                {{ isComingSoon ? $t.comingSoon : $t.outOfStock }}
              </vf-button>
            </template>
            <vf-button
              v-else
              aria-live="polite"
              class="w-full"
              :class="{ 'c-white': isSignInToBuy && theme }"
              :class-content="{ 'grow between gap-16': $feature.showPriceInCtas }"
              :disabled="pending || addToCartLoading || checkoutCtaLoading"
              :loading="addToCartLoading && !checkoutCtaLoading"
              :theme="isSignInToBuy ? theme : undefined"
              :variant="isSignInToBuy && theme ? 'themable' : 'primary'"
              @click="handleClick"
            >
              {{ ctaLabel }}
              <product-pricing
                v-if="$feature.showPriceInCtas"
                class-current="c-brand-5 "
                :currency="product.currency"
                hide-strikethrough-price
                :price="product.price"
              />
            </vf-button>
          </div>
        </div>
      </base-sticky>
    </div>
  </transition>
</template>

<script setup lang="ts">
const { showStickyHeader, stickyHeader } = useAppConfig().pages.pdp
const { card: { signInToBuyCta: { theme } } } = useAppConfig().components.product
const header = useHeaderStore()
const { $feature, $gtm, $t, $viewport } = useNuxtApp()
const {
  addToCart,
  addToCartLoading,
  attributeSelectionLabels,
  checkoutCtaLoading,
  findInStore,
  galleryItems,
  handleNextAttribute,
  isComingSoon,
  isNotifyMe,
  isOutOfStock,
  isRobisOnly,
  isSignInToBuy,
  pending,
  product,
  selectedVariant
} = usePdp()
const stickyState = useSticky()
const stickyContainerRef = ref<HTMLElement>()

const { height: windowHeight } = useWindowSize()
const { top: productDetailsCtaTop } = useElementBounding(() => stickyState.value.productDetailsCta)
const { y: productTitleY } = useElementBounding(useState('productDetailsTitleRef'))
const { height: stickyContainerHeight } = useElementBounding(stickyContainerRef)
const isCtaVisible = useDebounce(useElementVisibility(useState('productDetailsCtaRef')), 250)

const comingSoonDate = computed(() =>
  product.value?.targetPublishDate
    ? `${$t.comingSoon} – ${formatTargetPublishDate(product.value?.targetPublishDate, $feature.configProductComingSoonDateFormat)}`
    : ''
)

const ctaLabel = computed(() => {
  if (isNotifyMe.value && (isOutOfStock.value || isComingSoon.value))
    return `${$t.notifyMe}${comingSoonDate.value ? ` - ${comingSoonDate.value}` : ''}`

  if (isComingSoon.value) return `${$t.comingSoon}${comingSoonDate.value ? ` - ${comingSoonDate.value}` : ''}`
  if (isOutOfStock.value) return $t.outOfStock
  if (isSignInToBuy.value) return $t.signInToBuy
  if (isRobisOnly.value) return $t.reserveInStore
  if (!selectedVariant.value) return $t.selectSize
  return $t.addToCart
})

const showStickyTitle = computed(() =>
  $viewport.lg || ($viewport.sm && productTitleY.value > (windowHeight.value - stickyContainerHeight.value))
)

const stickyHeaderTop = computed(() =>
  !showStickyHeader && !header.pageScrolledUp
    ? '0'
    : productDetailsCtaTop.value < 0 && !header.pageScrolledUp
      ? pxToRem(header.sticky.offset)
      : pxToRem(header.sticky.margin)
)

const handleReserveInStore = () => {
  try {
    handleNextAttribute({ enableReserveInStore: true }, () => findInStore(attributeSelectionLabels.value, 'reserve'))
  }
  catch (e) {
    console.info(e)
  }
}

const handleClick = async () => {
  if (header.expanded) {
    header.expanded = false
    if (product.value) $gtm.push('pdpPage.onMobileNavigationInteraction', product.value.id, false)
  }
  else if (isRobisOnly.value) {
    handleReserveInStore()
  }
  else if (!selectedVariant.value && !isSignInToBuy.value) {
    handleNextAttribute({}, async () => await addToCart())
  }
  else {
    addToCart()
  }
}
</script>
